import { atom, computed } from "nanostores";
import { ApiError, getSettings, type HotbarSlot, putSettings, type SettingsDoc } from "../net/api";
import { describeError } from "../ui/errors";
import { toast } from "./stores";

export const SLOTS = 9;
/** The camera's angularSensibility at sensitivity 1: the value before plan 2g. */
export const BASE_ANGULAR_SENSIBILITY = 900;

export function defaultSettings(): SettingsDoc {
  return { hotbar: Array<HotbarSlot | null>(SLOTS).fill(null), mouse: { sensitivity: 1, invertY: false }, fov: 69 };
}

export const $settings = atom<SettingsDoc>(defaultSettings());
/** False until a read succeeds: every save replaces the whole document, so saving an unread one would wipe the stored settings (spec 2g §3.1). */
export const $settingsLoaded = atom(false);
/** True after a failed read, so the panel can say so instead of "loading". */
export const $settingsFailed = atom(false);
/** True while a save is in flight: edits wait, so responses cannot arrive out of order. */
export const $settingsSaving = atom(false);
/** The nine hotbar slots. Lives here, not in stores.ts, because this module already imports stores.ts. */
export const $hotbar = computed($settings, (s) => s.hotbar);
/** Asset codes in the hotbar this person cannot read (deleted, private, transferred away). */
export const $unavailable = atom<string[]>([]);
/**
 * The slider value being dragged, which the camera shows before it is saved. Only the dragged fields:
 * a preview left over (released at its start value, so no change event) must not hide other saved settings.
 */
export const $cameraPreview = atom<{ sensitivity?: number; fov?: number } | null>(null);
/** What the camera shows: the saved settings with any slider preview on top. */
export const $cameraSettings = computed([$settings, $cameraPreview], (s, p): SettingsDoc =>
  p ? { ...s, fov: p.fov ?? s.fov, mouse: { ...s.mouse, sensitivity: p.sensitivity ?? s.mouse.sensitivity } } : s);

export async function loadSettings(): Promise<void> {
  try {
    $settings.set(await getSettings());
    $settingsLoaded.set(true);
    $settingsFailed.set(false);
  } catch {
    $settingsLoaded.set(false);
    $settingsFailed.set(true);
  }
}

/** Optimistic save: show next at once, keep the server's answer, or restore the previous document. */
export async function saveSettings(next: SettingsDoc): Promise<boolean> {
  if (!$settingsLoaded.get()) return false;
  if ($settingsSaving.get()) { toast("設定儲存中"); return false; }
  $settingsSaving.set(true);
  const prev = $settings.get();
  $settings.set(next);
  try {
    $settings.set(await putSettings(next));
    return true;
  } catch (e) {
    $settings.set(prev);
    toast(`設定沒有存起來：${e instanceof ApiError ? describeError(e.code, e.message) : "無法連線到伺服器"}`, 5000);
    return false;
  } finally {
    $settingsSaving.set(false);
  }
}

const slotKey = (s: HotbarSlot | null): string => (s ? (s.kind === "asset" ? `asset:${s.code}` : `tool:${s.tool}`) : "");

export function inHotbar(bar: (HotbarSlot | null)[], code: string): boolean {
  return bar.some((s) => s?.kind === "asset" && s.code === code);
}

/** Puts item in slot i; if it already sits in another slot, that slot empties (one slot per item). */
export function placeInSlot(doc: SettingsDoc, i: number, item: HotbarSlot): SettingsDoc {
  const hotbar = doc.hotbar.map((s) => (slotKey(s) === slotKey(item) ? null : s));
  hotbar[i] = item;
  return { ...doc, hotbar };
}

export function swapSlots(doc: SettingsDoc, i: number, j: number): SettingsDoc {
  const hotbar = [...doc.hotbar];
  [hotbar[i], hotbar[j]] = [hotbar[j] ?? null, hotbar[i] ?? null];
  return { ...doc, hotbar };
}

export function clearSlot(doc: SettingsDoc, i: number): SettingsDoc {
  const hotbar = [...doc.hotbar];
  hotbar[i] = null;
  return { ...doc, hotbar };
}

/** Takes code out of the hotbar if it is there, otherwise puts it in the first free slot. */
export function toggleAsset(doc: SettingsDoc, code: string): { doc: SettingsDoc; result: "added" | "removed" | "full" } {
  const at = doc.hotbar.findIndex((s) => s?.kind === "asset" && s.code === code);
  if (at >= 0) return { doc: clearSlot(doc, at), result: "removed" };
  const free = doc.hotbar.indexOf(null);
  if (free < 0) return { doc, result: "full" };
  return { doc: placeInSlot(doc, free, { kind: "asset", code }), result: "added" };
}

export interface CameraParams { angularSensibility: number; fovRad: number; sensitivityY: number }

/** Camera values for s (spec 2g §2.1): look speed, vertical field of view, and the signed vertical look rate. */
export function cameraParams(s: SettingsDoc): CameraParams {
  const angularSensibility = BASE_ANGULAR_SENSIBILITY / s.mouse.sensitivity;
  return { angularSensibility, fovRad: (s.fov * Math.PI) / 180, sensitivityY: (s.mouse.invertY ? -1 : 1) / angularSensibility };
}
